import React, { useState, useEffect } from 'react';
import styles from './TimeLine.module.css';
import { formatDisplayDate, getRelativeDateLabel } from '../../utils/formatDay';
import { IAuditLog } from '../../hooks/patients/index.type';

interface AuditLogTimelineProps {
  data: IAuditLog[];
  setIsDayExpanded: (expanded: boolean) => void;
}

const AuditLogTimeline: React.FC<AuditLogTimelineProps> = ({ data, setIsDayExpanded }) => {
  const groupedLogs: Record<string, IAuditLog[]> = data?.reduce((acc, log) => {
    const timestampInMs = Number(log.createdTimestamp) * 1000;
    const date = new Date(timestampInMs).toISOString().split('T')[0];

    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {} as Record<string, IAuditLog[]>);

  const dates = Object.keys(groupedLogs);
  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];

  const initialExpandedGroups = dates.reduce((acc, date) => {
    acc[date] = date === firstDate;
    return acc;
  }, {} as Record<string, boolean>);

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(initialExpandedGroups);
  const [hasCalledAPI, setHasCalledAPI] = useState(false);

  useEffect(() => {
    const anyExpanded = Object.values(initialExpandedGroups).some(Boolean);
    setIsDayExpanded(anyExpanded);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isLastGroupExpanded = expandedGroups[lastDate];
      if (isLastGroupExpanded && !hasCalledAPI) {
        setIsDayExpanded(false); // parent triggers API
        setHasCalledAPI(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [expandedGroups, lastDate, hasCalledAPI]);

  const toggleGroup = (date: string) => {
    setExpandedGroups(prev => {
      const isNowExpanded = !prev[date];
      const newExpandedGroups = {
        ...prev,
        [date]: isNowExpanded,
      };

      const anyExpanded = Object.values(newExpandedGroups).some(Boolean);
      setIsDayExpanded(anyExpanded);

      if (date === lastDate && isNowExpanded && !hasCalledAPI) {
        setIsDayExpanded(false);
        setHasCalledAPI(true);
      }

      return newExpandedGroups;
    });
  };

  return (
    <div className={styles.timelineContainer}>
      {Object.entries(groupedLogs)?.map(([date, entries]) => {
        const isExpanded = expandedGroups[date];

        return (
          <div key={date}>
            <div className={styles.dateGroup}>
              <span>
                <button className={styles.arrowButton} onClick={() => toggleGroup(date)}>
                  {getRelativeDateLabel(date)}
                  <svg className={styles.arrowIcon}>
                    <use xlinkHref={`/assets/sprite.svg#icon-${isExpanded ? 'up-arrow' : 'down-arrow'}`} />
                  </svg>
                </button>
              </span>
            </div>

            {isExpanded &&
              entries?.map((entry, idx) => (
                <div key={idx} className={styles.timelineRow}>
                  <div className={styles.time}>
                    {new Date(Number(entry.createdTimestamp) * 1000).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </div>
                  <div className={styles.statusColumn}>
                    <span className={`${styles.dot} ${styles[entry?.status]}`} />
                  </div>
                  <div className={styles.details}>
                    <div className={styles.title}>{entry?.action}</div>
                    <div className={styles?.description}>{entry.message}</div>
                    {entry.status === 'error' && <div className={styles.errorMsg}>Error occurred</div>}
                  </div>
                </div>
              ))}
          </div>
        );
      })}
    </div>
  );
};

export default AuditLogTimeline;
